import type { Database, Record as SdkRecord } from '@servicenow/sdk-build-core'
import { CallExpressionShape, Plugin } from '@servicenow/sdk-build-core'
import {
    ACTIVITY_API_NAME,
    ACTIVITY_TABLE,
    LANE_DEFAULTS,
    LANE_TABLE,
    SYS_VARIABLE_VALUE_TABLE,
} from '../constants/playbook-plugin-constants'
import { DECISION_ACTIVITY_DEF_KEY, DECISION_BRANCHES_VAR_SYS_ID } from '../constants/activity-ootb-maps'
import { ActivityPlugin } from './activity-plugin'
import { ActivityShape, LaneShape } from '../utils/playbook-shapes'
import { buildLaneToShapeResult, resolveStartRule } from '../transforms/lane-config-transform'
import { isPlaybookUnsupported } from '../utils/transform-utils'
import { timerAttributesToRecord } from '../transforms/timer-attributes-transform'
import { processChildCollectionToRecord, type ChildCollectionTypeConfig } from '../transforms/child-collection-torecord'
import { resolveStartsAfterActivities, resolveStartsAfterBranches } from '../transforms/reference-resolver'
import { slugifyString } from '../../utils/slugify.js'
import { deduplicateNames } from '../../utils/unique-names.js'
import { stripHtmlTags } from '../utils/label-utils'
import { detectRedundantEdges } from '../utils/graph-utils'
import { type PillContext, resolvePlaybookPill } from '../utils/playbook-pill-resolver'
import type { ActivityIdentifier } from '../utils/playbook-pill-transformer'
import { validateOrderUniqueness } from '../utils/order-uniqueness-validator'

/**
 * Extract branch ID mappings from a processed Decision activity record.
 * Returns a map of fluentBranchId → generatedGuid, or undefined if not a Decision.
 */
export function extractDecisionBranchIdMap(
    callExpr: CallExpressionShape,
    resultRecord: SdkRecord
): Map<string, string> | undefined {
    const actDefKey = callExpr.getArgument(0, false)?.getCode()
    if (actDefKey !== DECISION_ACTIVITY_DEF_KEY) {
        return undefined
    }
    const related = resultRecord.getRelated()
    const branchesRecord = related.find(
        (r) =>
            r.getTable() === SYS_VARIABLE_VALUE_TABLE &&
            String(r.get('variable')?.getValue() ?? '') === DECISION_BRANCHES_VAR_SYS_ID
    )
    if (!branchesRecord) {
        return undefined
    }
    const branchesJsonStr = String(branchesRecord.get('value')?.getValue() ?? '')
    if (!branchesJsonStr) {
        return undefined
    }
    let branchesJson: { id: string }[]
    try {
        branchesJson = JSON.parse(branchesJsonStr)
    } catch {
        return undefined
    }
    // Decision uses 3-param API: Activity(def, config, inputs)
    // Branches are in arg2 (inputs), not nested in arg1
    const inputsObj = callExpr.getArgument(2)?.ifObject()
    const branchesArray = inputsObj?.get('branches')?.ifArray()
    if (!branchesArray) {
        return undefined
    }
    const fluentBranches = branchesArray.getElements()
    const mapping = new Map<string, string>()
    const count = Math.min(fluentBranches.length, branchesJson.length)
    for (let i = 0; i < count; i++) {
        const fluentElem = fluentBranches[i]
        const jsonElem = branchesJson[i]
        if (!fluentElem || !jsonElem) {
            continue
        }
        const fluentId = fluentElem.ifObject()?.get('id')?.ifString()?.getValue()
        const generatedId = jsonElem.id
        if (fluentId && generatedId) {
            mapping.set(fluentId, generatedId)
        }
    }
    return mapping.size > 0 ? mapping : undefined
}

export function laneRecordToShape(
    record: SdkRecord,
    database: Database,
    guidToVarName?: Map<string, string>,
    timerRecords?: SdkRecord[],
    globalActivityMap?: Map<string, ActivityIdentifier>,
    branchGuidMap?: Map<string, { activityVarName: string; fluentBranchId: string }>
): ReturnType<typeof Plugin.create> extends never
    ? never
    : { success: true; value: CallExpressionShape } | { success: false } {
    return buildLaneToShapeResult(record, database, guidToVarName, timerRecords, globalActivityMap, branchGuidMap)
}

export const LanePlugin = Plugin.create({
    name: 'LanePlugin',
    records: {
        [LANE_TABLE]: {
            relationships: {
                [ACTIVITY_TABLE]: {
                    via: 'lane',
                    descendant: true,
                },
            },
            toShape(record, { database }, guidToVarName?: Map<string, string>, timerRecords?: SdkRecord[]) {
                if (isPlaybookUnsupported(record, database)) {
                    return { success: false }
                }
                return buildLaneToShapeResult(record, database, guidToVarName, timerRecords)
            },
        },
    },
    shapes: [
        {
            shape: LaneShape,
            fileTypes: ['fluent'],
            async toRecord(laneShape, { factory, transform, diagnostics }) {
                const config = laneShape.getConfig()

                // Name resolution: the parent collection injects `uniqueName` — the de-duped
                // authoritative name (it equals the explicit name when there's no collision, and a
                // suffixed variant when it had to be de-duped). Prefer it; fall back to the explicit
                // name or a slugified label only when this lane is processed outside a collection.
                const injectedName = config.get('uniqueName')?.ifString()?.getValue()
                let computedName = injectedName
                if (!computedName) {
                    const explicitName = config.get('name')?.ifString()?.getValue()
                    const labelVal = config.get('label')?.ifString()?.getValue()
                    computedName = explicitName ?? (labelVal ? slugifyString(labelVal) : '')
                }

                const playbookBodyParamsNames: string[] = []
                const lanesParamName = laneShape.getPlaybookBodyParamsName()
                if (lanesParamName) {
                    playbookBodyParamsNames.push(lanesParamName)
                }

                const conditionPillContext: PillContext = {
                    format: 'condition',
                    factory,
                    diagnostics,
                    playbookBodyParamsNames,
                    allowedSources: {
                        paramsInputs: true,
                        paramsState: false,
                        paramsParentRecord: true,
                        previousActivityOutputs: true,
                        currentActivityLabelDesc: false,
                        automationPlan: false,
                    },
                }

                // Resolve conditionToRun pills before record creation (config.transform is synchronous)
                const conditionToRunShape = config.get('conditionToRun')
                const resolvedConditionToRun =
                    conditionToRunShape && !conditionToRunShape.isUndefined()
                        ? await resolvePlaybookPill(conditionToRunShape, conditionPillContext)
                        : undefined

                const laneRecord = await factory.createRecord({
                    source: laneShape,
                    table: LANE_TABLE,
                    explicitId: config.get('$id'),
                    properties: config.transform(({ $ }) => ({
                        label: $,
                        name: $.val(computedName),
                        order: $,
                        active: $.def(LANE_DEFAULTS.ACTIVE),
                        description: $.def(''),
                        start_rule: $.from('startRule').map((startRule) => resolveStartRule(startRule)),
                        start_rule_name: $.from('startRule').map((startRule) => resolveStartRule(startRule)),
                        starts_after_activities: $.from('startsAfterActivities'),
                        starts_after_lanes: $.from('startsAfterLanes'),
                        starts_after_branches: $.from('startsAfterBranches'),
                        condition_to_run: resolvedConditionToRun
                            ? $.val(resolvedConditionToRun)
                            : $.from('conditionToRun'),
                        restart_rule: $.from('restartRule'),
                        process_definition: $.from('processDefinition'),
                        permission: $.def(LANE_DEFAULTS.PERMISSION),
                    })),
                })

                // Create timer attributes record if startWithDelay is present.
                // The lane's timer record is merged into the final result alongside
                // activity records — activities under a delayed lane must still be processed.
                let laneTimerRecord: SdkRecord | undefined
                const startWithDelayShape = config.get('startWithDelay')
                if (startWithDelayShape && !startWithDelayShape.isUndefined()) {
                    const timerPillContext: PillContext = {
                        format: 'standard',
                        factory,
                        diagnostics,
                        playbookBodyParamsNames,
                        allowedSources: {
                            paramsInputs: true,
                            paramsState: true,
                            paramsParentRecord: true,
                            previousActivityOutputs: true,
                            currentActivityLabelDesc: false,
                            automationPlan: false,
                        },
                    }
                    const processDefinition = config.get('processDefinition')?.ifString()?.getValue() ?? ''
                    laneTimerRecord = await timerAttributesToRecord(
                        startWithDelayShape.asObject(),
                        laneRecord,
                        processDefinition,
                        'sys_pd_lane',
                        factory,
                        diagnostics,
                        timerPillContext
                    )
                }

                // Process activity children
                const activitiesArrow = laneShape.getActivities()
                // Add the activities-level arrow function parameter name so the
                // pill resolver can recognize references regardless of naming.
                const activitiesParamName = activitiesArrow?.getParameters()[0]?.getName()
                if (activitiesParamName && !playbookBodyParamsNames.includes(activitiesParamName)) {
                    playbookBodyParamsNames.push(activitiesParamName)
                }
                const laneNowIdKeyToGuid = laneShape.getNowIdKeyToGuid()
                const activityTypeConfig: ChildCollectionTypeConfig = {
                    configArgIndex: 1,
                    createShape: (source, cfg, context) => {
                        const callExpr = source.as(CallExpressionShape)
                        // Activity(def, config, inputs?, experienceProperties?)
                        // Questionnaire still uses 2-param API with inputs/questions in config
                        const arg2 = callExpr.getArgument(2)?.ifObject()
                        const arg3 = callExpr.getArgument(3)?.ifObject()
                        const argsObj = callExpr.getArgument(1)?.ifObject()
                        return new ActivityShape({
                            source,
                            activityDefinition: callExpr.getArgument(0, false),
                            config: cfg,
                            inputs: arg2 ?? argsObj?.get('inputs')?.ifObject(),
                            experienceProperties: arg3 ?? argsObj?.get('experienceProperties')?.ifObject(),
                            playbookBodyParamsNames,
                            ...(context?.nowIdKeyToGuid !== undefined
                                ? { nowIdKeyToGuid: context.nowIdKeyToGuid }
                                : {}),
                        })
                    },
                    childPlugin: ActivityPlugin,
                }
                const childTypeMap = new Map<string, ChildCollectionTypeConfig>([
                    [ACTIVITY_API_NAME, activityTypeConfig],
                    ['wfa.playbook.activity', activityTypeConfig],
                ])
                const activityRecords = activitiesArrow
                    ? await processChildCollectionToRecord({
                          collectionArrow: activitiesArrow,
                          parentRecord: laneRecord,
                          parentFkField: 'lane',
                          calleeNames: ['wfa.playbook.activity', ACTIVITY_API_NAME],
                          childTypeMap,
                          resolveReferences: resolveStartsAfterActivities,
                          resolvedRefField: 'startsAfterActivities',
                          resolveBranchReferences: resolveStartsAfterBranches,
                          resolvedBranchRefField: 'startsAfterBranches',
                          extractBranchIdMap: extractDecisionBranchIdMap,
                          validateChildren: (children, variables) => {
                              validateOrderUniqueness(children, diagnostics)
                              const redundantEdges = detectRedundantEdges(children, variables)
                              for (const {
                                  nodeId,
                                  redundantDepId,
                                  throughId,
                                  shape,
                                  nodeTypeLabel,
                              } of redundantEdges) {
                                  diagnostics.warn(
                                      shape,
                                      `${nodeTypeLabel} "${nodeId}" has a redundant start rule dependency on "${redundantDepId}" because it is already reachable through "${throughId}".`
                                  )
                              }
                          },
                          computePerChildMergeProps: (allConfigs, index) => {
                              // De-dupe against the full pool of names. Explicit names are kept;
                              // generated names (slugified labels) yield to anything already taken,
                              // so an auto-generated name never collides with an explicit one.
                              // Children here are all activities (calleeNames is activity-only), so
                              // strip HTML before slugifying — mirrors the platform BR that strips
                              // HTML from sys_pd_activity.label, keeping label/name aligned.
                              const entries = allConfigs.map((c) => {
                                  const explicit = c.get('name')?.ifString()?.getValue()
                                  return explicit
                                      ? { name: explicit, isExplicit: true }
                                      : {
                                            name: slugifyString(
                                                stripHtmlTags(c.get('label')?.ifString()?.getValue() ?? '')
                                            ),
                                            isExplicit: false,
                                        }
                              })
                              const uniqueName = deduplicateNames(entries)[index] ?? ''
                              const entry = entries[index]
                              if (entry?.isExplicit && entry.name !== uniqueName) {
                                  diagnostics.warn(
                                      allConfigs[index] ?? laneShape,
                                      `The name "${entry.name}" is already in use by another activity; it has been renamed to "${uniqueName}" to keep names unique.`
                                  )
                              }
                              const processDefinition = String(laneRecord.get('process_definition')?.getValue() ?? '')
                              if (!processDefinition) {
                                  diagnostics.error(laneShape, 'Process definition in lane is required')
                              }
                              return { uniqueName, processDefinition }
                          },
                          externalNowIdKeyToGuid: laneNowIdKeyToGuid,
                          transform,
                          diagnostics,
                          errorMessage: 'Lane activities function must return an object literal',
                          errorSource: laneShape,
                      })
                    : []

                return {
                    success: true,
                    value: laneRecord.with(...(laneTimerRecord ? [laneTimerRecord] : []), ...activityRecords),
                }
            },
        },
    ],
    docs: [],
})
